import React, { ChangeEvent, useEffect, useState } from "react";
import { Container, RadioButtonWithLabel } from "../../../components";
import { Button, Heading, IconName, Text, TextField } from "@telia/teddy";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";
import { CUSTOMER_TYPE, FORM_ID } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { RootState } from "../../../../../models";
import { getFeatureToggles } from "../../../app/state/selectors";
import ChoosePhoneNumber from "../../../checkout/components/organisms/ChoosePhoneNumber/ChoosePhoneNumber";
import ChooseSimCard from "../../../checkout/components/organisms/ChooseSimCard/ChooseSimCard";
import {
    chooseMemberPhoneNumber,
    chooseMemberPhoneNumberSwitchPanel,
    fetchAvailableNumbers,
} from "../../../checkout/state/actions/addMemberActions";
import { getCurrentSubscriptionMemberMessages } from "../../../checkout/state/selectors/accountSelectors";
import { getFormById } from "../../../checkout/state/selectors/formSelectors";
import { isPhoneNumberUsable } from "../../../checkout/utils";
import MemberCardStep from "../../../checkout/views/AddMember/MemberCardStep";
import { getFormattedPhoneNumber } from "../../../rapidform/formValidation";
import { setIsEsim, setSubscriptionDetails } from "../../state/actions/onboardingActions";
import {
    getSubscriptionDetails,
    isAllowedToProceed,
    isEsimSelected as getEsimSelected,
    isPortinSelected,
    isSubmitSubscriptionDetailsSuccess,
    getPhoneNumberCheckType,
    getAllowedExtraSim,
    isExistingPhoneNumberActive,
} from "../../state/selectors";
import "./SubscriptionDetails.less";

type Step = "phoneNumber" | "portinOwner" | "simCard";

const STEP_ORDER: Record<Step, number> = {
    phoneNumber: 1,
    portinOwner: 2,
    simCard: 3,
};

const SubscriptionDetails = () => {
    const dispatch = useDispatch();
    const [selectedStep, setSelectedStep] = useState<Step>("phoneNumber");
    const [portinOwnerSelectedOption, setPortinOwnerSelectedOption] = useState<
        "portin-myself" | "portin-company" | null
    >("portin-company");

    const isExtraSimOnboardingEnabled = useSelector(
        (state: RootState) => !!getFeatureToggles(state).enableExtraSimOnboarding,
    );
    const esimSelected = useSelector((state: RootState) => getEsimSelected(state));
    const subscription = useSelector((state: RootState) => getSubscriptionDetails(state));
    const onboardingForm = useSelector((state: RootState) => getFormById(state, FORM_ID.ADD_MEMBER));
    const submitSubscriptionDetailsSuccess = useSelector((state: RootState) =>
        isSubmitSubscriptionDetailsSuccess(state),
    );
    const allowedExtraSim = useSelector((state: RootState) => getAllowedExtraSim(state)) || [];
    const customerType = useSelector((state: RootState) => getPhoneNumberCheckType(state));
    const allowedToProceed = useSelector((state: RootState) => isAllowedToProceed(state));
    const currentSubscriptionMemberMessages = useSelector((state: RootState) =>
        getCurrentSubscriptionMemberMessages(state),
    );
    const [legalPhoneNumber, setLegalPhoneNumber] = useState("");
    const portinSelected = useSelector((state: RootState) => isPortinSelected(state));
    useEffect(() => {
        if (submitSubscriptionDetailsSuccess) {
            window.location.assign(PAGES.ONBOARDING_B2B.SUMMARY.URL);
        }
    }, [submitSubscriptionDetailsSuccess]);
    const isSubscriptionDraft = true;
    const subscriptionDetails = {
        isEsimSelected: esimSelected,
        legalPhoneNumber: legalPhoneNumber.replace(/\s/g, ""),
    };
    const selectedPhoneNumber = _get(onboardingForm, "values.member.number");
    const isPhoneNumberActive = useSelector((state: RootState) => isExistingPhoneNumberActive(state));
    const isNewNumberOrPortin =
        customerType === CUSTOMER_TYPE.NEW_OR_PORT_IN ||
        customerType === CUSTOMER_TYPE.PORT_IN ||
        customerType === CUSTOMER_TYPE.NEW;
    const shouldShowSimCardStep =
        !isPhoneNumberActive ||
        (!_isEmpty(allowedExtraSim) && customerType === CUSTOMER_TYPE.EXISTING) ||
        customerType !== CUSTOMER_TYPE.EXISTING;

    return (
        <div>
            <Container size="medium">
                <Heading className="subscription-details__heading" as="h1">
                    Abonnementsdetaljer
                </Heading>
                <Container className="beta-checkout__section">
                    <MemberCardStep
                        isDisabled={STEP_ORDER["phoneNumber"] > STEP_ORDER[selectedStep]}
                        label="Velg nummer"
                        onClick={() => setSelectedStep("phoneNumber")}
                        stepId="phoneNumber"
                        selectedStep={selectedStep}
                    >
                        <ChoosePhoneNumber
                            choosePhoneNumber={(phoneNumber) =>
                                dispatch(chooseMemberPhoneNumber(phoneNumber, subscription.id))
                            }
                            choosePhoneNumberSwitchPanel={(activePanel) =>
                                dispatch(chooseMemberPhoneNumberSwitchPanel(activePanel, subscription.id))
                            }
                            fetchAvailableNumbers={() => dispatch(fetchAvailableNumbers(true, subscription.id))}
                            selectedPhoneNumber={selectedPhoneNumber}
                            messages={currentSubscriptionMemberMessages}
                            subscriptionId={subscription.id as string}
                            isOnboarding={true}
                            isAllowedToProceed={currentSubscriptionMemberMessages.length > 0}
                            buttonLabel={"Lagre"}
                            buttonIcon={isSubscriptionDraft ? ("arrow-down" as IconName) : ("check-mark" as IconName)}
                            buttonIconPlacement={isSubscriptionDraft ? "right" : "left"}
                            onButtonClick={() => null}
                        />
                        <Button
                            disabled={!allowedToProceed}
                            className="subscription-details__continue-button"
                            onClick={() => {
                                if (shouldShowSimCardStep) {
                                    setSelectedStep("simCard");
                                } else {
                                    dispatch(setSubscriptionDetails(subscriptionDetails));
                                }
                            }}
                        >
                            Lagre
                        </Button>
                    </MemberCardStep>
                    {selectedPhoneNumber && (
                        <MemberCardStep
                            isDisabled={selectedStep === "phoneNumber"}
                            label="Velg SIM-kort"
                            onClick={() => setSelectedStep("simCard")}
                            stepId="simCard"
                            selectedStep={selectedStep}
                        >
                            <ChooseSimCard
                                isEsimOnly={false}
                                isEsimSelected={esimSelected}
                                isEsimCompatible={true}
                                isOrderExtraSimEnabled={isExtraSimOnboardingEnabled && !_isEmpty(allowedExtraSim)}
                                shouldShowSimOptions={
                                    isNewNumberOrPortin ||
                                    (!isPhoneNumberActive && customerType === CUSTOMER_TYPE.EXISTING)
                                }
                                esimType="ESIM-QR"
                                setPersonalDetailsValue={(key, value) => {
                                    if (key === "isEsimSelected") {
                                        dispatch(setIsEsim(value as boolean));
                                    }
                                }}
                                onButtonClick={() => {
                                    if (customerType !== CUSTOMER_TYPE.PORT_IN) {
                                        dispatch(setSubscriptionDetails(subscriptionDetails));
                                    }
                                    setSelectedStep("portinOwner");
                                }}
                                buttonLabel={"Lagre"}
                                buttonIcon={
                                    isSubscriptionDraft ? ("arrow-right" as IconName) : ("check-mark" as IconName)
                                }
                                buttonIconPlacement={isSubscriptionDraft ? "right" : "left"}
                                subscriptionId={subscription.id || "placeholder"}
                                isOnboarding={true}
                                phoneNumber={selectedPhoneNumber}
                            />
                        </MemberCardStep>
                    )}
                    {portinSelected && (
                        <MemberCardStep
                            isDisabled={STEP_ORDER["portinOwner"] > STEP_ORDER[selectedStep]}
                            label="Oppgi juridisk eier av nummeret"
                            onClick={() => setSelectedStep("portinOwner")}
                            stepId="portinOwner"
                            selectedStep={selectedStep}
                        >
                            <div className="subscription-details__legal-owner">
                                <Text mb="100">
                                    Hvis nummeret tilhører en bedrift, kan du oppgi telefonnummeret til din nærmeste
                                    leder. Vi sender da en SMS med en forespørsel om å godkjenne eierskiftet.
                                </Text>
                                <RadioButtonWithLabel
                                    value="portin-myself"
                                    onChange={(event) => {
                                        setPortinOwnerSelectedOption(
                                            event.target.value as "portin-myself" | "portin-company",
                                        );
                                    }}
                                    name="onboarding-portin-myself"
                                    checked={portinOwnerSelectedOption === "portin-myself"}
                                    label={`Meg selv (${subscription?.subscription.productUser?.firstName} ${subscription?.subscription.productUser?.lastName})`}
                                    data-di-mask
                                />
                                <RadioButtonWithLabel
                                    value="portin-company"
                                    onChange={(event) => {
                                        setPortinOwnerSelectedOption(
                                            event.target.value as "portin-myself" | "portin-company",
                                        );
                                    }}
                                    name="onboarding-portin-company"
                                    checked={portinOwnerSelectedOption === "portin-company"}
                                    label="En bedrift eller annen privatperson"
                                />
                                {portinOwnerSelectedOption === "portin-company" && (
                                    <div className="subscription-details__subaccept-company">
                                        <TextField isValid={isPhoneNumberUsable(legalPhoneNumber.replace(/\s/g, ""))}>
                                            <TextField.Label>Mobilnummer</TextField.Label>
                                            <TextField.Input
                                                type="tel"
                                                value={legalPhoneNumber}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                    const formattedNumber = getFormattedPhoneNumber(event.target.value);
                                                    setLegalPhoneNumber(formattedNumber);
                                                }}
                                                placeholder=""
                                                data-di-mask
                                            />
                                        </TextField>
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={() => {
                                    dispatch(setSubscriptionDetails(subscriptionDetails));
                                }}
                            >
                                Lagre
                            </Button>
                        </MemberCardStep>
                    )}
                </Container>
            </Container>
        </div>
    );
};

export default SubscriptionDetails;
